import { addGameObject } from '../entity';
import { setVisibilityState } from '../simulate';
import { CANVAS_TEXT_CAP_MARGIN_RATIO, DEFAULT_TEXT_STYLE, SHAPE_STYLES } from '../constants';
import { COLORS } from '../constants-colors';

import {
  ServerEvent_EntityProps,
  ServerEvent_TextObject,
  ObjectType,
  type ServerEvent_TextObject2,
} from '../shared/api_shared_types';
import {
  Entity,
  VisibilityState,
  GameManager,
  GameObjectController,
  Shape,
  V2,
  type TextObject2,
  RenderItemType,
  TextAlignHor,
  type TextStyle,
  type TextObjectSimple,
} from '../types';
import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageItem,
  type RenderPackageRect,
  type RenderPackageText,
} from '../render_package';
import * as TextLayoutController from '../controllers/text_layout_controller';
import logger from '../controllers/logger';

export function controller(): GameObjectController {
  return {
    getNew: getZeroTextObject,
    sync: null,
    update,
    createPackage,
  };
}

export function getZeroTextObject(ent: Entity) {
  let newTextObject: TextObjectSimple = {
    ...ent,
    objectType: ObjectType.TEXTOBJECT_SIMPLE,
    textOverflow: 'ellipsis',

    defaultTextStyle: DEFAULT_TEXT_STYLE,

    text: '',
    visibleText: '',
    _ellipsisWidth: 0,
  };

  return newTextObject;
}

export function initTextObject(
  manager: GameManager,
  textObject: TextObjectSimple,
): TextObjectSimple {
  if (textObject.shape === Shape.NONE) {
    textObject.shape = Shape.RECT;
  }

  setVisibilityState(textObject, VisibilityState.DEFAULT);

  return textObject;
}

export function createAndAddLocalTextObjectSimple(
  manager: GameManager,
  ent: Entity,
  props: Partial<TextObjectSimple>,
): TextObjectSimple {
  const tObj = {
    ...getZeroTextObject(ent),
    ...props,
  };

  addGameObject(manager, tObj);

  initTextObject(manager, tObj);

  setText(tObj, props.text, true);

  return tObj;
}

export function setText(tObj: TextObjectSimple, text: string, force = false) {
  if (!force && text === tObj.text) return;

  const ctx = tObj.manager.textRenderCtx;
  tObj.text = text;

  if (tObj.textOverflow === 'ellipsis') {
    tObj.visibleText = createEllipsis(ctx, text, tObj.defaultTextStyle, tObj.width);
    tObj._ellipsisWidth = tObj.width;
    tObj.height = tObj.defaultTextStyle.fontSize;
  } else {
    tObj.width = TextLayoutController.getTextWidth(
      ctx,
      tObj.defaultTextStyle.fontId,
      tObj.defaultTextStyle.fontSize,
      text,
    );
    tObj.visibleText = text;
    tObj.height = tObj.defaultTextStyle.fontSize;
  }

  tObj.isDirty = true;
}

function createEllipsis(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  text: string,
  textStyle: TextStyle,
  maxWidth: number,
): string {
  const measure = (t: string) =>
    TextLayoutController.getTextWidth(ctx, textStyle.fontId, textStyle.fontSize, t);

  if (measure(text) <= maxWidth) return text;

  const ellipsis = '...';
  if (measure(ellipsis) >= maxWidth) return ellipsis;

  // Binary search for the longest prefix that fits with ellipsis appended.
  // A binary search on n elements converges in at most ⌈log₂(n)⌉ steps, so
  // the loop count is a hard upper bound — it can never run indefinitely.
  let low = 0;
  let high = text.length - 1;
  const maxSteps = Math.ceil(Math.log2(text.length + 1));
  for (let step = 0; step < maxSteps; step++) {
    const mid = Math.ceil((low + high) / 2);
    if (measure(text.slice(0, mid) + ellipsis) <= maxWidth) {
      low = mid;
    } else {
      high = mid - 1;
    }
  }
  return text.slice(0, low) + ellipsis;
}

function update(tObj: TextObjectSimple): boolean {
  let isDirty = false;

  if (tObj.textOverflow === 'ellipsis' && tObj.width !== tObj._ellipsisWidth) {
    const ctx = tObj.manager.textRenderCtx;
    tObj.visibleText = createEllipsis(ctx, tObj.text, tObj.defaultTextStyle, tObj.width);
    tObj._ellipsisWidth = tObj.width;

    isDirty = true;
  }

  return isDirty;
}

export function createPackage(
  textObject: TextObjectSimple,
  position: V2,
  scale: number,
): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  if (false) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: position.x,
      positionY: position.y,
      width: textObject.width,
      height: textObject.height,
      opacity: textObject.opacity,

      shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: COLORS.BLACK_50 },
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect);
  }

  let rText: RenderPackageText = {
    type: RenderItemType.text,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: position.x - scale * textObject.width * 0.5,
    positionY:
      position.y -
      scale * textObject.height * 0.5 +
      scale * textObject.defaultTextStyle.fontSize * CANVAS_TEXT_CAP_MARGIN_RATIO,
    hAlign: TextAlignHor.LEFT,
    fillColor: textObject.defaultTextStyle.color,
    strokeWeight: 0,
    strokeColor: [0, 0, 0],
    size: textObject.defaultTextStyle.fontSize,
    text: textObject.visibleText,
    fontId: textObject.defaultTextStyle.fontId,
    shadow: null,
    opacity: 1,
  };
  rPackage.push(rText);

  return rPackage;
}
